/**
 * @interface InteractionShortcut
 * @description Define la estructura para un atajo de teclado personalizado.
 * Almacena la tecla principal y el estado de las teclas modificadoras.
 */
export interface InteractionShortcut {
  key: string;
  altKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
}

/**
 * @interface AiSettingsInterface
 * @description Define la estructura y el tipado para el objeto de configuración de la IA del chatbot.
 * Este "contrato" asegura la consistencia de los datos de configuración a través de toda la aplicación,
 * proporcionando seguridad de tipos y autocompletado en el editor de código.
 */
export interface AiSettingsInterface {
  /**
   * @property {'blue' | 'gradient' | 'violet'} theme
   * @description El tema de color seleccionado para la interfaz del chatbot.
   */
  theme: 'blue' | 'gradient' | 'violet';
  /**
   * @property {boolean} voiceInteractionEnabled
   * @description Habilita o deshabilita globalmente la interacción por voz (escuchar y hablar).
   */
  voiceInteractionEnabled: boolean;
  /**
   * @property {boolean} interactiveVoiceEnabled
   * @description Si la interacción por voz está activa, este flag determina si el chatbot
   * responderá automáticamente con voz después de escuchar al usuario.
   */
  interactiveVoiceEnabled: boolean;
  /**
   * @property {'professional' | 'friendly' | ...} tone
   * @description Define el tono de voz y estilo de las respuestas generadas por el modelo de lenguaje.
   */
  tone: 'professional' | 'friendly' | 'informal' | 'empathetic' | 'persuasive' | 'humorous' | 'ceremonious' | 'direct';
  /**
   * @property {string} username
   * @description El nombre de usuario para personalizar las interacciones. Puede estar vacío.
   */
  username: string;
  /**
   * @property {InteractionShortcut} interactionShortcut
   * @description El atajo de teclado personalizado para una interacción rápida.
   */
  interactionShortcut: InteractionShortcut;
  /**
   * @property {boolean} consoleEnabled
   * @description Habilita o deshabilita la visualización de la sección de consola de depuración en la UI.
   */
  consoleEnabled: boolean;
  /**
   * @property {boolean} logEnabled
   * @description Si la consola está habilitada, este flag controla si se muestran los mensajes de tipo 'log'.
   */
  logEnabled: boolean;
  /**
   * @property {boolean} infoEnabled
   * @description Si la consola está habilitada, este flag controla si se muestran los mensajes de tipo 'error'.
   */
  errorEnabled: boolean;
  /**
   * @property {boolean} warningEnabled
   * @description Si la consola está habilitada, este flag controla si se muestran los mensajes de tipo 'warning'.
   */
  warningEnabled: boolean;
  /**
   * @property {boolean} debugEnabled
   * @description Si la consola está habilitada, este flag controla si se muestran los mensajes de tipo 'debug'.
   */
  debugEnabled: boolean;
}

/**
 * @const DEFAULT_CHATBOT_SETTINGS
 * @description Un objeto constante que provee una configuración por defecto para el chatbot.
 * Se utiliza para inicializar el estado la primera vez que un usuario abre la aplicación
 * o cuando la configuración guardada en `localStorage` no se encuentra o está corrupta.
 * @type {AiSettingsInterface}
 */
export const DEFAULT_TOOLS_AI_SETTINGS: AiSettingsInterface = {
  theme: 'gradient',
  voiceInteractionEnabled: true,
  interactiveVoiceEnabled: true,
  tone: 'professional',
  username: '',
  interactionShortcut: {key: 'j', altKey: true, ctrlKey: false, shiftKey: false}, // Atajo por defecto: Alt + J
  consoleEnabled: false,
  logEnabled: false,
  errorEnabled: false,
  warningEnabled: false,
  debugEnabled: false,
};
